/**
 * Riff Tuner — Pro entitlement state.
 *
 * Module-level state, so every component that calls usePro() shares the same
 * refs. Do not create this per-component.
 *
 * Usage:
 *   const { isPro, ready, upgrade, restorePurchases } = usePro();
 *   if (isPro.value) { ...unlock DADGAD... }
 */

import { ref, readonly, computed } from 'vue';
import {
  configureRevenueCat,
  fetchCustomerInfo,
  fetchCurrentOffering,
  lifetimePackage,
  onCustomerInfoChange,
  purchase,
  restore,
  hasPro,
  PurchaseCancelledError,
  describePurchaseError,
} from '@/services/revenuecat';

/* ------------------------------------------------------------------ */
/* Shared state                                                        */
/* ------------------------------------------------------------------ */

const isPro = ref(false);
const customerInfo = ref(null);
const offering = ref(null);

/** False until the first entitlement check completes. */
const ready = ref(false);
/** True while a purchase or restore is in flight. */
const busy = ref(false);

let unsubscribe = null;
let initPromise = null;

/* ------------------------------------------------------------------ */
/* Bootstrap                                                           */
/* ------------------------------------------------------------------ */

/**
 * Configure the SDK, read the current entitlement, and start listening for
 * changes. Call once from main.js. Idempotent.
 *
 * This never throws. If the store is unreachable at launch the app opens in
 * free mode and the listener corrects it once connectivity returns. A tuner
 * that refuses to start because a billing server is down is a broken tuner.
 *
 * @returns {Promise<void>}
 */
export async function initPro() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      await configureRevenueCat();

      unsubscribe = await onCustomerInfoChange((info) => {
        customerInfo.value = info;
        isPro.value = hasPro(info);
      });

      const info = await fetchCustomerInfo();
      customerInfo.value = info;
      isPro.value = hasPro(info);

      // Warm the offering cache so the paywall opens instantly later.
      void loadOffering();
    } catch (err) {
      console.error('[RevenueCat] init failed, continuing in free mode', err);
    } finally {
      ready.value = true;
    }
  })();

  return initPromise;
}

export async function teardownPro() {
  if (unsubscribe) await unsubscribe();
  unsubscribe = null;
  initPromise = null;
}

async function loadOffering() {
  try {
    offering.value = await fetchCurrentOffering();
  } catch (err) {
    console.error('[RevenueCat] could not load offering', err);
    offering.value = null;
  }
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export function usePro() {
  /** The Lifetime package, or null if the store hasn't returned products. */
  const lifetime = computed(() => lifetimePackage(offering.value));

  /** Localised price string, already formatted by the store. Never hardcode this. */
  const lifetimePrice = computed(
    () => lifetime.value?.product.priceString ?? null,
  );

  /**
   * Buy Lifetime directly, without the RevenueCat paywall UI. Use only for a
   * custom upgrade surface; prefer presentPaywall() from
   * useRevenueCatPaywall for the main flow.
   *
   * @returns {Promise<{status: 'purchased'|'cancelled'|'error', message?: string}>}
   */
  async function upgrade() {
    if (busy.value) return { status: 'cancelled' };

    if (!lifetime.value) {
      await loadOffering();
      if (!lifetime.value) {
        return {
          status: 'error',
          message: 'Pro is not available right now. Check your connection and try again.',
        };
      }
    }

    busy.value = true;
    try {
      const info = await purchase(lifetime.value);
      customerInfo.value = info;
      isPro.value = hasPro(info);
      return { status: 'purchased' };
    } catch (err) {
      if (err instanceof PurchaseCancelledError) return { status: 'cancelled' };
      return { status: 'error', message: describePurchaseError(err) };
    } finally {
      busy.value = false;
    }
  }

  /**
   * Restore a previous purchase.
   *
   * Returns whether Pro was actually found — the SDK resolves successfully
   * even when there was nothing to restore, so you must check the entitlement
   * yourself to avoid telling someone "Restored!" when nothing happened.
   *
   * @returns {Promise<{status: string, found: boolean, message?: string}>}
   */
  async function restorePurchases() {
    if (busy.value) return { status: 'cancelled', found: false };

    busy.value = true;
    try {
      const info = await restore();
      customerInfo.value = info;
      isPro.value = hasPro(info);
      return { status: 'purchased', found: isPro.value };
    } catch (err) {
      return { status: 'error', message: describePurchaseError(err), found: false };
    } finally {
      busy.value = false;
    }
  }

  /** Force a fresh entitlement read. Useful on app resume. */
  async function refresh() {
    try {
      const info = await fetchCustomerInfo();
      customerInfo.value = info;
      isPro.value = hasPro(info);
    } catch (err) {
      console.error('[RevenueCat] refresh failed', err);
    }
  }

  return {
    isPro: readonly(isPro),
    ready: readonly(ready),
    busy: readonly(busy),
    customerInfo: readonly(customerInfo),
    lifetime,
    lifetimePrice,
    upgrade,
    restorePurchases,
    refresh,
    reloadOffering: loadOffering,
  };
}
